import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ParcelInfo from 'components/generics/Map/ParcelInfo';
import { ExploitationContext } from 'utils/context';
import { ORGANIC_STATES } from 'utils/GlobalVariables';
import './Map.style.scss';
import MapInteractionHook from './MapInteraction.hook';
import { BrushButton, MapGroupsPanel, MapItemsPanel, ModeSwitch } from './MapPanels';
import {
  modes,
  emptyList,
  previousCultureOffsets,
  useCultureMode,
  useGroupMode,
  useMapParcelsData,
  useSoilMode,
} from './useMapParcelsModes';

function MapParcels({
  updateParcel,
  forecastCampaign,
  name = 'map',
  fromProcedure = false,
  parcelListData,
  stackedParcelsRequestsRef,
  setIsAllowedSubmit,
  hasPendingChanges,
  refreshData,
}) {
  const { exploitationContext } = useContext(ExploitationContext);
  const { t } = useTranslation();
  const [mode, setMode] = useState(modes.cultures);

  const forecastYear = forecastCampaign.startYear + 1;
  const minYear = forecastYear - Math.max(...previousCultureOffsets);
  const maxYear = forecastYear;
  const cultures = exploitationContext?.cultures ?? emptyList;
  const groundTypes = exploitationContext?.groundTypes ?? emptyList;
  const isEditable = !fromProcedure && !!stackedParcelsRequestsRef;
  const isCultureMode = mode === modes.cultures;
  const isGroupMode = mode === modes.groups;

  const { parcelsData, stackRequest } = useMapParcelsData({
    parcels: exploitationContext?.parcels,
    cultures,
    groundTypes,
    forecastCampaign,
    fromProcedure,
    parcelListData,
    stackedParcelsRequestsRef,
    setIsAllowedSubmit,
    hasPendingChanges,
  });

  const cultureMode = useCultureMode({ cultures, forecastYear, stackRequest });
  const soilMode = useSoilMode({ groundTypes, stackRequest });
  const groupMode = useGroupMode({ parcelsData, refreshData });
  const activeMode = { cultures: cultureMode, groundTypes: soilMode, groups: groupMode }[mode];
  const itemsMode = isCultureMode ? cultureMode : soilMode;
  const isBrushActive = isEditable && !isGroupMode && !!itemsMode.selection.selectedItem;

  const onParcelClick = (parcel, clickedYear, _minYear, clickedParcel) => {
    if (!parcel) return;
    if (isEditable && activeMode.onParcelClick(parcel, clickedYear, clickedParcel ?? parcel)) {
      return;
    }
    updateParcel(parcel.id);
  };

  const mapState = MapInteractionHook({
    mapId: name,
    parcelsData,
    initialYear: forecastYear - 1,
    minYear,
    maxYear,
    getCultureForParcel: activeMode.getCultureForParcel,
    getPolygonStyleParams: activeMode.getPolygonStyleParams,
    onParcelClick,
    dependencies: [mode, groupMode.draft],
  });

  const hasParcelOrganic = parcelsData.some(
    (parcel) =>
      parcel?.organicState === ORGANIC_STATES.organic ||
      parcel?.organicState === ORGANIC_STATES.conversion,
  );

  const handleModeChange = (_, value) => {
    if (!value) return;
    setMode(value);
    groupMode.reset();
  };

  return (
    <div>
      {hasParcelOrganic && (
        <div className="legend_map_container" style={{ margin: '0 80px' }}>
          <div className="legend_title">{t('label.mapParcelLegend')}</div>
          <div className="legend_map_row">
            <div className="outline_legend">
              <div className="stroke conversion"></div>
              <p>{t('simulation.result.common.bioConversion')}</p>
            </div>
            <div className="outline_legend">
              <div className="stroke organic"></div>
              <p>{t('simulation.common.bioCulture')}</p>
            </div>
            <div className="outline_legend">
              <div className="stroke conventional"></div>
              <p>{t('label.conventionalCulture')}</p>
            </div>
          </div>
        </div>
      )}
      <div className="tab_container">
        {!fromProcedure && (
          <div className="details_container details_container--parcels">
            {isGroupMode ? (
              <MapGroupsPanel groupMode={groupMode} />
            ) : (
              <MapItemsPanel
                isCultureMode={isCultureMode}
                forecastYear={forecastYear}
                forecastCampaign={forecastCampaign}
                pendingRequests={
                  hasPendingChanges ? (stackedParcelsRequestsRef?.current ?? emptyList) : null
                }
                items={isCultureMode ? cultures : groundTypes}
                parcelsData={parcelsData}
                mapState={mapState}
                selection={itemsMode.selection}
                isEditable={isEditable}
                colorsByName={itemsMode.colorsByName}
              />
            )}
          </div>
        )}
        <div ref={mapState.mapElement} className="map_container" style={{ position: 'relative' }}>
          <div
            id={name}
            className={isBrushActive ? 'map_element modeBrush' : 'map_element'}
            onMouseOut={mapState.clearPopup}
          ></div>

          {!fromProcedure && (
            <div
              style={{
                position: 'absolute',
                top: 8,
                left: 44,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <ModeSwitch
                mode={mode}
                onChange={handleModeChange}
                isGroupsDisabled={hasPendingChanges}
              />
              {isBrushActive && (
                <BrushButton
                  name={itemsMode.selection.selectedItem.name}
                  color={itemsMode.getItemColor(itemsMode.selection.selectedItem.name)}
                  onFinish={() => itemsMode.selection.select(null)}
                />
              )}
            </div>
          )}

          {mapState.parcelInfo && mapState.modalCoords && (
            <div ref={mapState.popup} style={mapState.getPopupStyle()}>
              <div
                ref={mapState.popupContentRef}
                className="ol-popup"
                style={mapState.getPopupInnerStyle()}
              >
                <ParcelInfo
                  parcelInfo={mapState.parcelInfo}
                  from="mapParcels"
                  forecastCampaign={forecastCampaign}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MapParcels;
